import { useEffect, useState } from "react";
import { cashnatorFloat } from "../../../components/utils/functions";
import MobileSearchBar from "../../ui/MobileSearchBar";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useCouponsSearch } from "./useCouponsSearch";
import { MobileCreateCouponSheet, MobileCouponDetailSheet } from "./MobileCouponSheets";

function couponKindLabel(kind) {
  if (kind === "percent") return "Percentual";
  if (kind === "amount") return "Quantia";
  return "";
}

function formatCouponValue(coupon) {
  if (coupon?.kind === "amount") return cashnatorFloat(coupon?.value);
  if (coupon?.kind === "percent") return `${coupon?.value}%`;
  return "";
}

export function couponSubtitle(coupon) {
  const parts = [couponKindLabel(coupon?.kind), formatCouponValue(coupon)];
  if (coupon?.isActive) parts.push("Ativo");
  else parts.push("Inativo");
  if (coupon?.timesUsed != null) parts.push(`${coupon.timesUsed} uso(s)`);
  return parts.filter(Boolean).join(" · ");
}

export default function MobileCouponsList({ user }) {
  const { coupons, loading, error, search, refresh } = useCouponsSearch(user);
  const [code, setCode] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    setHasSearched(true);
    search();
  }, [search]);

  const handleSearch = (rawQ) => {
    const value = typeof rawQ === "string" ? rawQ : code;
    setCode(value);
    setHasSearched(true);
    search(value);
  };

  const openDetail = (coupon) => {
    setSelectedCoupon(coupon);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedCoupon(null);
  };

  const handleCreated = () => {
    setCreateOpen(false);
    setHasSearched(true);
    refresh();
  };

  const handleCouponChanged = () => {
    closeDetail();
    refresh();
  };

  const searchLabel = code.trim() ? "Pesquisar" : "Pesquisar todos";

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg hover:bg-[#b47c06]"
        >
          Cadastrar cupom
        </button>
        <button
          type="button"
          onClick={() => {
            setHasSearched(true);
            refresh();
          }}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-primary/50 px-3 text-sm font-medium text-laranjinha/80 hover:bg-monao"
        >
          Atualizar
        </button>
      </div>

      <MobileSearchBar
        value={code}
        onChange={setCode}
        onSubmit={handleSearch}
        submitLabel={searchLabel}
        placeholder="Código do cupom"
      />

      {loading ? (
        <div aria-label="Carregando cupons" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError
          message={error.message ?? "Erro ao buscar cupons."}
          onRetry={refresh}
        />
      ) : hasSearched && coupons.length === 0 ? (
        <MobileEmpty
          title="Nenhum cupom encontrado"
          message="Toque em Pesquisar para carregar a lista."
        />
      ) : coupons.length > 0 ? (
        <MobileList>
          {coupons.map((item) => (
            <MobileListItem
              key={item._id}
              title={item.code ?? "Sem código"}
              subtitle={couponSubtitle(item)}
              onClick={() => openDetail(item)}
            />
          ))}
        </MobileList>
      ) : null}

      <MobileCreateCouponSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        user={user}
        onCreated={handleCreated}
      />

      <MobileCouponDetailSheet
        open={detailOpen}
        onClose={closeDetail}
        coupon={selectedCoupon}
        user={user}
        onChanged={handleCouponChanged}
      />
    </div>
  );
}
